// src/components/admin/AdminLekhiContentNew.js
// NEW Lekhi content screen: upload topic files, status grid, preview, delete, replace audio.
import React, { useState, useEffect, useCallback } from 'react';
import {
  LEKHI_TOPICS,
  LEKHI_LEVELS,
  getLekhiTopicContentKey,
  getLekhiTopicNumber,
  getLekhiTopicContentIndex,
  getLekhiTopicContent,
  saveLekhiTopicFiles,
  deleteLekhiTopicFiles,
  replaceLekhiTopicAudio
} from '../../services/firebase/firestoreService';
import { getSubjectName, getTopicNameMr } from '../../services/lekhiTopicNames';
import TextView from '../lekhi/TextView';
import MCQs from '../lekhi/MCQs';
import AudioView from '../lekhi/AudioView';

const SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];
const EXCLUDED_TOPICS = ['current_affairs_national', 'current_affairs_maharashtra'];
const LEVEL_LABEL = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

const CHIPS = [
  { type: 'studyText', short: 'S', label: 'StudyText' },
  { type: 'revisionText', short: 'R', label: 'RevisionText' },
  { type: 'cheatSheet', short: 'C', label: 'CheatSheet' },
  { type: 'mcqs', short: 'M', label: 'MCQs' },
  { type: 'audio1', short: 'A1', label: 'Audio 1' },
  { type: 'audio2', short: 'A2', label: 'Audio 2' }
];
const TEXT_TYPES = ['studyText', 'revisionText', 'cheatSheet'];
const AUDIO_TYPES = ['audio1', 'audio2'];
const TYPE_ORDER = CHIPS.map(c => c.type);

// e.g. 9.Samanarthi_Easy_StudyText.txt
const FILE_NAME_RE = /^(\d+)\.([^_]+)_(easy|medium|hard)_(studytext|revisiontext|cheatsheet|mcqs)\.txt$/i;
const FILE_TYPE_MAP = { studytext: 'studyText', revisiontext: 'revisionText', cheatsheet: 'cheatSheet', mcqs: 'mcqs' };
const MAX_FILE_CHARS = 60000; // keeps every record far below Firestore's 1 MB limit
const SUCCESS_MSG_MS = 10000; // success messages disappear after 10 seconds

const usedTopics = (subject) => LEKHI_TOPICS[subject].filter(t => !EXCLUDED_TOPICS.includes(t));
const topicLabel = (subject, topic) => `${getLekhiTopicNumber(subject, topic)}. ${getTopicNameMr(topic)}`;
const chipLabel = (type) => CHIPS.find(c => c.type === type)?.label || type;

// Success messages clear themselves; error messages stay until the next action
const useAutoHideSuccess = (msg, setMsg) => {
  useEffect(() => {
    if (!msg || msg.type !== 'success') return undefined;
    const id = setTimeout(() => setMsg(null), SUCCESS_MSG_MS);
    return () => clearTimeout(id);
  }, [msg, setMsg]);
};

// ---------- MCQ file check ----------
const validateMcqs = (raw, subject, topic, level) => {
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return { errors: ['Not valid JSON.'], value: null };
  }
  const list = Array.isArray(parsed) ? parsed : parsed?.mcqs;
  if (!Array.isArray(list)) return { errors: ['No "mcqs" list found.'], value: null };

  const errors = [];
  if (list.length !== 30) errors.push(`Has ${list.length} MCQs (must be exactly 30).`);

  const clean = [];
  const seen = new Set();
  list.forEach((q, i) => {
    const n = i + 1;
    if (!q || typeof q !== 'object') { errors.push(`Q${n}: not a question.`); return; }
    if (q.subject !== subject) errors.push(`Q${n}: subject is "${q.subject}", expected "${subject}".`);
    if (q.topic !== topic) errors.push(`Q${n}: topic is "${q.topic}", expected "${topic}".`);
    if (q.level !== level) errors.push(`Q${n}: level is "${q.level}", expected "${level}".`);
    if (typeof q.question !== 'string' || !q.question.trim()) errors.push(`Q${n}: question is empty.`);
    const opts = q.options || {};
    ['A', 'B', 'C', 'D'].forEach(k => {
      if (typeof opts[k] !== 'string' || !opts[k].trim()) errors.push(`Q${n}: option ${k} is missing.`);
    });
    if (!['A', 'B', 'C', 'D'].includes(q.correctAnswer)) errors.push(`Q${n}: correctAnswer must be A, B, C or D.`);
    if (typeof q.explanation !== 'string' || !q.explanation.trim()) errors.push(`Q${n}: explanation is empty.`);
    const qKey = (q.question || '').trim();
    if (qKey && seen.has(qKey)) errors.push(`Q${n}: duplicate question.`);
    seen.add(qKey);
    clean.push({
      n,
      question: q.question || '',
      options: { A: opts.A || '', B: opts.B || '', C: opts.C || '', D: opts.D || '' },
      correctAnswer: q.correctAnswer,
      explanation: q.explanation || '',
      tags: Array.isArray(q.tags) ? q.tags : []
    });
  });
  return { errors, value: clean };
};

// ---------- Styles ----------
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
const thStyle = { padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', background: '#f8f9fa', whiteSpace: 'nowrap' };
const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' };
const statBox = { background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', padding: '6px 10px', minWidth: '70px' };
const statNum = { fontSize: '16px', fontWeight: 700, color: '#222' };
const statLbl = { fontSize: '11px', color: '#888' };
const pillBtn = (active) => ({
  padding: '6px 14px', borderRadius: '16px', border: '1px solid #ddd', cursor: 'pointer',
  background: active ? '#6366f1' : '#fff', color: active ? '#fff' : '#333', fontWeight: 600, fontSize: '13px'
});

export default function AdminLekhiContentNew() {
  const [index, setIndex] = useState({});
  const [indexLoading, setIndexLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Upload
  const [upSubject, setUpSubject] = useState('grammar');
  const [upTopic, setUpTopic] = useState(usedTopics('grammar')[0]);
  const [checkRows, setCheckRows] = useState([]);
  const [checking, setChecking] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  // Grid
  const [gridSubject, setGridSubject] = useState('grammar');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [gridMsg, setGridMsg] = useState(null);
  const [preview, setPreview] = useState(null);

  // Replace audio
  const [raSubject, setRaSubject] = useState('grammar');
  const [raTopic, setRaTopic] = useState(usedTopics('grammar')[0]);
  const [raLevel, setRaLevel] = useState('easy');
  const [raSlot, setRaSlot] = useState('audio1');
  const [raFile, setRaFile] = useState(null);
  const [raMsg, setRaMsg] = useState(null);

  useAutoHideSuccess(uploadMsg, setUploadMsg);
  useAutoHideSuccess(gridMsg, setGridMsg);
  useAutoHideSuccess(raMsg, setRaMsg);

  const reloadIndex = useCallback(async () => {
    setIndexLoading(true);
    try {
      setIndex(await getLekhiTopicContentIndex());
    } catch (e) {
      setGridMsg({ type: 'error', text: `Could not load status: ${e.message}` });
    } finally {
      setIndexLoading(false);
    }
  }, []);

  useEffect(() => { reloadIndex(); }, [reloadIndex]);

  // ================= UPLOAD =================
  const handleUpSubject = (s) => {
    setUpSubject(s);
    setUpTopic(usedTopics(s)[0]);
    setCheckRows([]);
    setUploadMsg(null);
  };

  const handleUpTopic = (t) => {
    setUpTopic(t);
    setCheckRows([]);
    setUploadMsg(null);
  };

  const handleFilesChosen = async (e) => {
    const fileList = Array.from(e.target.files || []);
    e.target.value = '';
    if (fileList.length === 0) return;
    setChecking(true);
    setUploadMsg(null);

    const topicNumber = getLekhiTopicNumber(upSubject, upTopic);
    const rows = [];
    const seen = {};

    for (const file of fileList) {
      const m = file.name.match(FILE_NAME_RE);
      if (!m) {
        rows.push({ name: file.name, status: 'ignored', note: 'Not a content file — will be skipped.', errors: [], warnings: [] });
        continue;
      }
      const num = parseInt(m[1], 10);
      const level = m[3].toLowerCase();
      const type = FILE_TYPE_MAP[m[4].toLowerCase()];
      const row = { name: file.name, level, type, errors: [], warnings: [], value: null };

      if (num !== topicNumber) {
        row.errors.push(`File is topic ${num}, but the selected topic is number ${topicNumber}.`);
      }
      const dupKey = `${level}_${type}`;
      if (seen[dupKey]) row.errors.push('Same level + type selected twice.');
      seen[dupKey] = true;

      let raw = '';
      try {
        raw = (await file.text()).replace(/^\uFEFF/, '');
      } catch (err) {
        row.errors.push('Could not read the file.');
      }

      if (type === 'mcqs') {
        const r = validateMcqs(raw, upSubject, upTopic, level);
        row.errors.push(...r.errors);
        row.value = r.value;
      } else {
        const text = raw.trim();
        if (!text) row.errors.push('File is empty.');
        row.value = text;
      }

      if (row.value && JSON.stringify(row.value).length > MAX_FILE_CHARS) {
        row.errors.push('File is too large.');
      }

      const existing = index[getLekhiTopicContentKey(upSubject, upTopic, level)] || {};
      if (existing[type]) row.warnings.push('Already uploaded — will be replaced.');
      const missingAudio = AUDIO_TYPES.filter(a => !existing[a]).map(a => chipLabel(a));
      if (missingAudio.length > 0) {
        row.warnings.push(`${LEVEL_LABEL[level]} has no ${missingAudio.join(' & ')} yet.`);
      }

      row.status = row.errors.length > 0 ? 'error' : 'ok';
      rows.push(row);
    }

    rows.sort((a, b) => {
      if (a.status === 'ignored' && b.status !== 'ignored') return 1;
      if (b.status === 'ignored' && a.status !== 'ignored') return -1;
      if (a.status === 'ignored') return 0;
      const lv = LEKHI_LEVELS.indexOf(a.level) - LEKHI_LEVELS.indexOf(b.level);
      return lv !== 0 ? lv : TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type);
    });

    setCheckRows(rows);
    setChecking(false);
  };

  const okRows = checkRows.filter(r => r.status === 'ok');
  const hasErrors = checkRows.some(r => r.status === 'error');

  const handleUpload = async () => {
    if (hasErrors || okRows.length === 0) return;
    const name = `${getSubjectName(upSubject, 'en')} — ${topicLabel(upSubject, upTopic)}`;
    if (!window.confirm(`Upload ${okRows.length} file(s) for ${name}?`)) return;
    setBusy(true);
    setUploadMsg(null);
    try {
      const n = await saveLekhiTopicFiles(
        upSubject,
        upTopic,
        okRows.map(r => ({ level: r.level, type: r.type, value: r.value }))
      );
      setUploadMsg({ type: 'success', text: `✓ Uploaded ${n} file(s) for ${name}.` });
      setCheckRows([]);
      await reloadIndex();
    } catch (e) {
      setUploadMsg({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setBusy(false);
    }
  };

  // ================= TOTALS =================
  const computeTotals = (subjects) => {
    const t = { ready: 0, slots: 0, texts: 0, mcqs: 0, audios: 0 };
    subjects.forEach(s => {
      usedTopics(s).forEach(topic => {
        LEKHI_LEVELS.forEach(level => {
          t.slots++;
          const item = index[getLekhiTopicContentKey(s, topic, level)] || {};
          if (CHIPS.every(c => item[c.type])) t.ready++;
          TEXT_TYPES.forEach(tt => { if (item[tt]) t.texts++; });
          if (item.mcqs) t.mcqs += typeof item.mcqs === 'number' ? item.mcqs : 30;
          AUDIO_TYPES.forEach(a => { if (item[a]) t.audios++; });
        });
      });
    });
    return t;
  };

  const renderTotalsRow = (label, t) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
      <div style={{ width: '120px', fontSize: '13px', fontWeight: 700, color: '#444' }}>{label}</div>
      <div style={statBox}><div style={statNum}>{t.ready}/{t.slots}</div><div style={statLbl}>Topic-levels ready</div></div>
      <div style={statBox}><div style={statNum}>{t.texts}</div><div style={statLbl}>Texts</div></div>
      <div style={statBox}><div style={statNum}>{t.mcqs}</div><div style={statLbl}>MCQs</div></div>
      <div style={statBox}><div style={statNum}>{t.audios}</div><div style={statLbl}>Audios</div></div>
    </div>
  );

  // ================= GRID =================
  const selKey = (subject, topic, level, type) => `${subject}|${topic}|${level}|${type}`;
  const selectedCount = Object.keys(selected).length;

  const openPreview = async (subject, topic, level, type) => {
    setBusy(true);
    setGridMsg(null);
    try {
      const data = await getLekhiTopicContent(subject, topic, level);
      if (!data) throw new Error('Record not found.');
      setPreview({ subject, topic, level, type, data });
    } catch (e) {
      setGridMsg({ type: 'error', text: `Preview error: ${e.message}` });
    } finally {
      setBusy(false);
    }
  };

  const handleChipTap = (subject, topic, level, type, present) => {
    if (!present || busy) return;
    if (selectMode) {
      const k = selKey(subject, topic, level, type);
      setSelected(prev => {
        const next = { ...prev };
        if (next[k]) delete next[k];
        else next[k] = true;
        return next;
      });
    } else {
      openPreview(subject, topic, level, type);
    }
  };

  const toggleSelectMode = () => {
    setSelectMode(m => !m);
    setSelected({});
    setGridMsg(null);
  };

  const handleDeleteSelected = async () => {
    const items = Object.keys(selected).map(k => {
      const [subject, topic, level, type] = k.split('|');
      return { subject, topic, level, type };
    });
    if (items.length === 0) return;
    const lines = items.map(i =>
      `• ${getSubjectName(i.subject, 'en')} · ${topicLabel(i.subject, i.topic)} · ${LEVEL_LABEL[i.level]} · ${chipLabel(i.type)}`
    ).join('\n');
    if (!window.confirm(`Delete these ${items.length} file(s)? This cannot be undone.\n\n${lines}`)) return;
    setBusy(true);
    setGridMsg(null);
    try {
      const n = await deleteLekhiTopicFiles(items);
      setGridMsg({ type: 'success', text: `✓ Deleted ${n} file(s).` });
      setSelected({});
      setSelectMode(false);
      await reloadIndex();
    } catch (e) {
      setGridMsg({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setBusy(false);
    }
  };

  const renderChip = (subject, topic, level, chip) => {
    const item = index[getLekhiTopicContentKey(subject, topic, level)] || {};
    const present = !!item[chip.type];
    const isSel = !!selected[selKey(subject, topic, level, chip.type)];
    const style = {
      display: 'inline-block',
      minWidth: '22px',
      textAlign: 'center',
      fontSize: '11px',
      fontWeight: 700,
      padding: '2px 4px',
      margin: '1px',
      borderRadius: '4px',
      cursor: present ? 'pointer' : 'default',
      border: isSel ? '2px solid #dc3545' : '2px solid transparent',
      background: isSel ? '#fee2e2' : present ? '#dcfce7' : '#f1f3f5',
      color: isSel ? '#991b1b' : present ? '#15803d' : '#adb5bd'
    };
    return (
      <span
        key={chip.type}
        style={style}
        title={`${chip.label}${present ? '' : ' (missing)'}`}
        onClick={() => handleChipTap(subject, topic, level, chip.type, present)}
      >
        {chip.short}
      </span>
    );
  };

  // ================= REPLACE AUDIO =================
  const handleRaSubject = (s) => {
    setRaSubject(s);
    setRaTopic(usedTopics(s)[0]);
    setRaMsg(null);
  };

  const handleReplaceAudio = async () => {
    if (!raFile) { setRaMsg({ type: 'error', text: 'Choose an audio file.' }); return; }
    const where = `${getSubjectName(raSubject, 'en')} · ${topicLabel(raSubject, raTopic)} · ${LEVEL_LABEL[raLevel]} · ${chipLabel(raSlot)}`;
    const existing = index[getLekhiTopicContentKey(raSubject, raTopic, raLevel)] || {};
    const verb = existing[raSlot] ? 'Replace' : 'Add';
    if (!window.confirm(`${verb} ${where} with "${raFile.name}"?`)) return;
    setBusy(true);
    setRaMsg(null);
    try {
      await replaceLekhiTopicAudio(raSubject, raTopic, raLevel, raSlot, raFile);
      setRaMsg({ type: 'success', text: `✓ Audio saved for ${where}.` });
      setRaFile(null);
      const input = document.getElementById('ra-file-input');
      if (input) input.value = '';
      await reloadIndex();
    } catch (e) {
      setRaMsg({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setBusy(false);
    }
  };

  // ================= PREVIEW (full screen, exactly as students see it) =================
  if (preview) {
    const close = () => setPreview(null);
    const { type, data, topic, level } = preview;
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#f8f9fa', zIndex: 1000, overflowY: 'auto' }}>
        {TEXT_TYPES.includes(type) && (
          <TextView textContent={{ text: data[type] || '' }} language="mr" onBack={close} onComplete={close} />
        )}
        {type === 'mcqs' && (
          <MCQs
            mcqs={(data.mcqs || []).map(q => ({ ...q, id: `preview_${q.n}` }))}
            language="mr"
            onBack={close}
            onComplete={close}
          />
        )}
        {AUDIO_TYPES.includes(type) && (
          <AudioView
            audio={{ audioURL: data[`${type}URL`] }}
            blockLabel={`${chipLabel(type)} · ${LEVEL_LABEL[level]}`}
            topicName={getTopicNameMr(topic)}
            language="mr"
            onBack={close}
            onComplete={close}
          />
        )}
      </div>
    );
  }

  const allTotals = computeTotals(SUBJECTS);
  const subjectTotals = computeTotals([gridSubject]);

  return (
    <div>
      <div className="admin-section-title">Lekhi Content (New)</div>

      {/* ===== UPLOAD ===== */}
      <div className="admin-card">
        <h3 style={{ marginTop: 0 }}>Upload topic files</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
          Pick subject and topic, then select 1–12 files (e.g. 9.Samanarthi_Easy_StudyText.txt). Other files are ignored. Nothing is saved until every file passes the check.
        </p>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <div className="admin-form-group" style={{ minWidth: '160px' }}>
            <label>Subject</label>
            <select value={upSubject} onChange={e => handleUpSubject(e.target.value)} disabled={busy}>
              {SUBJECTS.map(s => <option key={s} value={s}>{getSubjectName(s, 'en')}</option>)}
            </select>
          </div>
          <div className="admin-form-group" style={{ minWidth: '220px' }}>
            <label>Topic</label>
            <select value={upTopic} onChange={e => handleUpTopic(e.target.value)} disabled={busy}>
              {usedTopics(upSubject).map(t => (
                <option key={t} value={t}>{topicLabel(upSubject, t)} ({t})</option>
              ))}
            </select>
          </div>
        </div>

        <input type="file" multiple accept=".txt" onChange={handleFilesChosen} disabled={busy || checking} />
        {checking && <p style={{ fontSize: '13px', color: '#888' }}>Checking files...</p>}

        {checkRows.length > 0 && (
          <div style={{ marginTop: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr><th style={thStyle}>File</th><th style={thStyle}>Check</th></tr>
              </thead>
              <tbody>
                {checkRows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{r.name}</td>
                    <td style={tdStyle}>
                      {r.status === 'ignored' && <span style={{ color: '#888' }}>– {r.note}</span>}
                      {r.status === 'ok' && <span style={{ color: '#15803d', fontWeight: 700 }}>✓ OK</span>}
                      {r.status === 'error' && (
                        <div style={{ color: '#dc3545' }}>
                          {r.errors.slice(0, 5).map((err, ei) => <div key={ei}>✗ {err}</div>)}
                          {r.errors.length > 5 && <div>…and {r.errors.length - 5} more</div>}
                        </div>
                      )}
                      {r.status !== 'ignored' && r.warnings.length > 0 && (
                        <div style={{ color: '#b45309', fontSize: '12px', marginTop: '2px' }}>
                          {r.warnings.map((w, wi) => <div key={wi}>⚠ {w}</div>)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '12px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="admin-btn admin-btn-primary" onClick={handleUpload}
                disabled={busy || hasErrors || okRows.length === 0}>
                {busy ? 'Uploading...' : `Upload ${okRows.length} file(s)`}
              </button>
              <button className="admin-btn admin-btn-secondary" onClick={() => setCheckRows([])} disabled={busy}>
                Clear
              </button>
              {hasErrors && <span style={{ color: '#dc3545', fontSize: '13px' }}>Fix the ✗ files first, then select the files again.</span>}
            </div>
          </div>
        )}

        {uploadMsg && <div className={`admin-status-msg ${uploadMsg.type}`}>{uploadMsg.text}</div>}
      </div>

      {/* ===== STATUS GRID ===== */}
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0 }}>Content status</h3>
          <button className="admin-btn admin-btn-secondary" onClick={reloadIndex} disabled={busy || indexLoading}>
            {indexLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        <div style={{ marginTop: '12px' }}>
          {renderTotalsRow('All subjects', allTotals)}
          {renderTotalsRow(getSubjectName(gridSubject, 'en'), subjectTotals)}
        </div>

        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '10px 0' }}>
          {SUBJECTS.map(s => (
            <button key={s} style={pillBtn(gridSubject === s)} onClick={() => setGridSubject(s)}>
              {getSubjectName(s, 'en')}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
          <button className={`admin-btn ${selectMode ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
            onClick={toggleSelectMode} disabled={busy}>
            {selectMode ? 'Cancel selection' : 'Select to delete'}
          </button>
          {selectMode && (
            <button className="admin-btn admin-btn-danger" onClick={handleDeleteSelected}
              disabled={busy || selectedCount === 0}>
              Delete selected ({selectedCount})
            </button>
          )}
          <span style={{ fontSize: '12px', color: '#888' }}>
            {selectMode ? 'Tap green chips to select them.' : 'Tap a green chip to preview it as a student.'}
            {' '}S=StudyText R=RevisionText C=CheatSheet M=MCQs A1/A2=Audio
          </span>
        </div>

        {gridMsg && <div className={`admin-status-msg ${gridMsg.type}`}>{gridMsg.text}</div>}

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Topic</th>
                {LEKHI_LEVELS.map(l => <th key={l} style={thStyle}>{LEVEL_LABEL[l]}</th>)}
              </tr>
            </thead>
            <tbody>
              {usedTopics(gridSubject).map(topic => (
                <tr key={topic}>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 600 }}>{topicLabel(gridSubject, topic)}</div>
                    <div style={{ fontSize: '11px', color: '#999' }}>{topic}</div>
                  </td>
                  {LEKHI_LEVELS.map(level => {
                    const item = index[getLekhiTopicContentKey(gridSubject, topic, level)] || {};
                    const complete = CHIPS.every(c => item[c.type]);
                    return (
                      <td key={level} style={{ ...tdStyle, whiteSpace: 'nowrap', background: complete ? '#f0fdf4' : undefined }}>
                        {CHIPS.map(chip => renderChip(gridSubject, topic, level, chip))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {gridSubject === 'gk' && (
          <p style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>GK: 13 topics (current affairs not part of daily Lekhi).</p>
        )}
      </div>

      {/* ===== REPLACE AUDIO ===== */}
      <div className="admin-card">
        <h3 style={{ marginTop: 0 }}>Add / replace audio</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
          Max 10 MB (mp3, m4a, mp4). The old audio file is kept in storage, only the link changes.
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <div className="admin-form-group" style={{ minWidth: '160px' }}>
            <label>Subject</label>
            <select value={raSubject} onChange={e => handleRaSubject(e.target.value)} disabled={busy}>
              {SUBJECTS.map(s => <option key={s} value={s}>{getSubjectName(s, 'en')}</option>)}
            </select>
          </div>
          <div className="admin-form-group" style={{ minWidth: '220px' }}>
            <label>Topic</label>
            <select value={raTopic} onChange={e => { setRaTopic(e.target.value); setRaMsg(null); }} disabled={busy}>
              {usedTopics(raSubject).map(t => (
                <option key={t} value={t}>{topicLabel(raSubject, t)} ({t})</option>
              ))}
            </select>
          </div>
          <div className="admin-form-group">
            <label>Level</label>
            <select value={raLevel} onChange={e => { setRaLevel(e.target.value); setRaMsg(null); }} disabled={busy}>
              {LEKHI_LEVELS.map(l => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
            </select>
          </div>
          <div className="admin-form-group">
            <label>Audio</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {AUDIO_TYPES.map(a => (
                <button key={a} type="button" style={pillBtn(raSlot === a)}
                  onClick={() => { setRaSlot(a); setRaMsg(null); }} disabled={busy}>
                  {chipLabel(a)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <input id="ra-file-input" type="file" accept="audio/*,video/mp4"
          onChange={e => { setRaFile(e.target.files?.[0] || null); setRaMsg(null); }} disabled={busy} />
        {raFile && (
          <p style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
            {raFile.name} — {(raFile.size / (1024 * 1024)).toFixed(2)} MB
          </p>
        )}
        <div style={{ marginTop: '10px' }}>
          <button className="admin-btn admin-btn-primary" onClick={handleReplaceAudio} disabled={busy || !raFile}>
            {busy ? 'Uploading...' : 'Save audio'}
          </button>
        </div>
        {raMsg && <div className={`admin-status-msg ${raMsg.type}`}>{raMsg.text}</div>}
      </div>
    </div>
  );
}